import { IntegrationsTracking } from "@/lib/tracking/integrations";
import { Button, Sheet, SheetContent } from "@altimateai/lego";
import styles from "./styles.module.scss";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deleteDBTIntegration, getIntegrations } from "../../helpers/apis";
import { ComponentLoader } from "../../components/loader";
import { DataTable } from "../../components/tables";
import { NoIntegration } from "./NoIntegration";
import { GET_INTEGRATIONS, SEARCH_PARAMS } from "./constants";
import { IntegrationDetails } from "./IntegrationDetailsModal";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ColorInsightTags } from "../../components/tags";
import { useAppState } from "@/modules/app/useAppContext";
import { isOwner, isAdmin } from "@/services/auth";
import { IconTrash } from "@tabler/icons-react";
import usePaginate from "@/components/paginate/usePaginate";
import { Paginate } from "@/components/paginate";
import { Stack } from "@altimateai/lego";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { TableHeaderType } from "@/components/tables/types";
import { Integration } from "@/types/integration";
import { DbtConnectionMigrationBanner } from "./DbtConnectionMigrationBanner";

export const IntegrationList = () => {
  useBreadcrumb("Integrations", {
    defaultParents: [{ label: "Settings", pathname: "/settings", noLink: true }],
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { dbtCoreIntegrationId, dbtCoreIntegrationEnv } = useParams();
  const { loggedinUser: user, isDemoTenant, enableCloudIntegration } = useAppState();
  const [searchParams] = useSearchParams();
  const { updateSearchParamsByKey } = useFiltersBySearchParams();

  const navigateToIntegration = useCallback(
    (integrationId: string, envName: string) =>
      navigate(`/settings/integrations/${integrationId}/${envName}?${searchParams}`),
    [navigate, searchParams]
  );

  // Initialize pagination from URL params on mount
  const urlPageOnMount = searchParams.get(SEARCH_PARAMS.PAGE);
  const urlPageSizeOnMount = searchParams.get(SEARCH_PARAMS.PAGE_SIZE);
  const initialPage = urlPageOnMount ? parseInt(urlPageOnMount, 10) - 1 : 0; // Convert to 0-based
  const initialPageSize = urlPageSizeOnMount ? parseInt(urlPageSizeOnMount, 10) : 20;

  const { page, setPage, pageSize, updatePageSize } = usePaginate({
    page: initialPage,
    size: initialPageSize,
  });

  useEffect(() => {
    if (dbtCoreIntegrationId && dbtCoreIntegrationEnv) return;
    if (!dbtCoreIntegrationId && !dbtCoreIntegrationEnv) return;
    navigate(`/settings/integrations?${searchParams}`);
  }, [dbtCoreIntegrationEnv, dbtCoreIntegrationId, navigate, searchParams]);

  // Sync pagination state with URL params when URL changes (but not on initial mount since we initialize from URL)
  const isInitialMount = useRef(true);
  useEffect(() => {
    // Skip on initial mount since we already initialized from URL
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const urlPage = searchParams.get(SEARCH_PARAMS.PAGE);
    const urlPageSize = searchParams.get(SEARCH_PARAMS.PAGE_SIZE);

    if (urlPage) {
      const pageNum = parseInt(urlPage, 10) - 1; // Convert to 0-based index
      if (pageNum >= 0 && pageNum !== page) {
        setPage(pageNum);
      }
    } else if (page !== 0) {
      // If URL doesn't have page param but state is not 0, reset to 0
      setPage(0);
    }

    if (urlPageSize) {
      const size = parseInt(urlPageSize, 10);
      if (size > 0 && size !== pageSize) {
        updatePageSize(size);
      }
    } else if (pageSize !== 20) {
      // If URL doesn't have page_size param but state is not default, reset to default
      updatePageSize(20);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Wrapper handlers that update both state and URL
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    updateSearchParamsByKey({
      [SEARCH_PARAMS.PAGE]: newPage > 0 ? (newPage + 1).toString() : undefined, // Convert to 1-based for URL
    });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    // Reset to first page when page size changes
    setPage(0);
    updatePageSize(newPageSize);
    updateSearchParamsByKey({
      [SEARCH_PARAMS.PAGE_SIZE]: newPageSize !== 20 ? newPageSize.toString() : undefined,
      [SEARCH_PARAMS.PAGE]: undefined, // Remove page param (defaults to page 1 in URL)
    });
  };

  const { data: integrations, isLoading } = useQuery({
    queryKey: [GET_INTEGRATIONS],
    queryFn: getIntegrations,
  });

  const { mutate } = useMutation({
    mutationFn: ({ id }: { id: string; integrationType: string }) => deleteDBTIntegration(id),
    onSuccess: (_data, { id, integrationType }) => {
      IntegrationsTracking.deleted(id, integrationType);
      queryClient.invalidateQueries({ queryKey: [GET_INTEGRATIONS] });
    },
  });

  const header = useMemo((): TableHeaderType<Integration>[] => {
    const baseHeaders: TableHeaderType<Integration>[] = [
      {
        id: 1,
        label: "Name of Integration",
        uid: "name",
        width: enableCloudIntegration ? "28%" : "36%",
        render: (item: Integration) => <div className="al-truncate al-text-brand">{item.name}</div>,
      },
      {
        id: 2,
        label: "Type of Integration",
        uid: "type",
        width: enableCloudIntegration ? "14%" : "18%",
        render: (item: Integration) => (
          <div className="al-truncate">
            {item.integration_type === "dbt_core" ? "dbt Core" : "dbt Cloud"}
          </div>
        ),
      },
    ];

    // Add Connection Name column if enableCloudIntegration is enabled
    if (enableCloudIntegration) {
      baseHeaders.push({
        id: 3,
        label: "Connection Name",
        uid: "connection_name",
        width: "18%",
        render: (item: Integration) => (
          <div className="al-truncate">{item.connection_name || "-"}</div>
        ),
      });
    }

    baseHeaders.push(
      {
        id: enableCloudIntegration ? 4 : 3,
        label: "Environment Names",
        tooltipInfo:
          "If you don't have a deployment environment with a successful run in the last 30 days, it won't be able to sync the artifacts",
        uid: "environments",
        width: enableCloudIntegration ? "24%" : "28%",
        render: (item: Integration) => (
          <div className="al-truncate">
            <ColorInsightTags
              tags={item.environments?.map(env => env.name) || []}
              onClick={envName => navigateToIntegration(item.id, envName)}
            />
          </div>
        ),
      },
      {
        id: enableCloudIntegration ? 5 : 4,
        label: "Action",
        uid: "action",
        width: enableCloudIntegration ? "16%" : "18%",
        render: (item: Integration) => {
          const canDelete = isOwner() || isAdmin();
          const isDisabled = isDemoTenant || !canDelete;
          const title = isDemoTenant
            ? "Delete is disabled in demo mode"
            : !canDelete
              ? "Only owners and admins can delete integrations"
              : "Delete integration";
          return !item?.is_deleted ? (
            <div className="al-truncate">
              <Button
                variant="ghost"
                size="icon"
                title={title}
                className={isDisabled ? "al-cursor-not-allowed al-opacity-50" : ""}
                onClick={() => {
                  if (!isDisabled) mutate({ id: item.id, integrationType: item.integration_type });
                }}
                aria-label={title}
              >
                <IconTrash />
              </Button>
            </div>
          ) : (
            <div className="al-truncate">
              Scheduled for deletion (typically takes up to 24 hours)
            </div>
          );
        },
      }
    );

    return baseHeaders;
  }, [enableCloudIntegration, isDemoTenant, navigateToIntegration]);

  // Paginated integrations
  const paginatedIntegrations = useMemo(() => {
    if (!integrations) return [];
    return integrations.slice(page * pageSize, (page + 1) * pageSize);
  }, [integrations, page, pageSize]);

  const totalItems = integrations?.length || 0;
  const numPages = Math.ceil(totalItems / pageSize);

  if (isLoading) return <ComponentLoader label="Loading integrations..." />;
  return (
    <div className={styles.integrations}>
      <Stack align="center" justify="between" className="mb-4" aria-label="Integrations header">
        <div className={styles.title}>Integrations</div>
        {isOwner() && !enableCloudIntegration ? (
          <Button
            className={styles.button}
            disabled={!user?.permission?.includes("INTEGRATION_CREATE")}
            onClick={() => navigate("/settings/integrations/new")}
            aria-label="Add new integration"
          >
            Add Integration
          </Button>
        ) : null}
      </Stack>
      <DbtConnectionMigrationBanner />
      {(integrations?.length ?? 0) === 0 ? (
        <NoIntegration
          toggleAddIntegration={() => navigate("/settings/integrations/new")}
          disabled={!user?.permission?.includes("INTEGRATION_CREATE")}
          hideAddButton={enableCloudIntegration ?? false}
        />
      ) : (
        <Stack direction="column" gap="lg">
          <DataTable
            header={header}
            data={{ items: paginatedIntegrations }}
            idKey={item => item.id}
          />
          <Paginate
            itemCount={totalItems}
            numPages={numPages}
            page={page}
            pageSize={pageSize}
            onPageClick={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            showPageSize
          />
        </Stack>
      )}
      <Sheet
        open={!!(dbtCoreIntegrationId && dbtCoreIntegrationEnv)}
        onOpenChange={open => !open && navigate(`/settings/integrations?${searchParams}`)}
      >
        <SheetContent side="right" className="!al-w-[446px]" aria-label="Integration details modal">
          <IntegrationDetails
            dbtCoreIntegrationId={dbtCoreIntegrationId ?? ""}
            dbtCoreIntegrationEnv={dbtCoreIntegrationEnv ?? ""}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
};
